package com.flowyride.app.di

import com.flowyride.app.config.AppConfig
import com.flowyride.app.config.platformDefaultServerUrl
import com.flowyride.core.domain.account.AccountRepository
import com.flowyride.core.domain.account.AdminRepository
import com.flowyride.core.device.NavigationDevice
import com.flowyride.core.device.NavigationSettings
import com.flowyride.core.device.platformNavigationDevice
import com.flowyride.core.domain.favorite.FavoriteRepository
import com.flowyride.core.domain.place.SavedPlaceRepository
import com.flowyride.core.domain.place.SavedPlaces
import com.flowyride.core.domain.navigation.ActiveNavigation
import com.flowyride.core.fileexport.OpenedFiles
import com.flowyride.core.domain.navigation.NavigationPreferences
import com.flowyride.core.domain.gpx.GpxWriter
import com.flowyride.core.domain.geocoding.Geocoder
import com.flowyride.core.domain.loop.LoopGenerator
import com.flowyride.core.domain.routing.RouteEngineRegistry
import com.flowyride.core.domain.usecase.BuildGpxUseCase
import com.flowyride.core.domain.speedcamera.SpeedCameraRepository
import com.flowyride.core.domain.usecase.ComputeRouteUseCase
import com.flowyride.core.domain.usecase.GenerateLoopUseCase
import com.flowyride.core.domain.usecase.GetSpeedCamerasUseCase
import com.flowyride.core.domain.usecase.ReadGpxUseCase
import com.flowyride.core.domain.usecase.ReverseGeocodeUseCase
import com.flowyride.core.domain.usecase.SearchPlacesUseCase
import com.flowyride.core.network.NetworkConfig
import com.flowyride.core.network.createHttpClient
import com.flowyride.data.account.HttpAccountRepository
import com.flowyride.data.account.HttpAdminRepository
import com.flowyride.data.account.ServerSession
import com.flowyride.data.account.platformTokenStore
import com.flowyride.data.account.withServerSession
import com.flowyride.data.favorites.HttpFavoriteRepository
import com.flowyride.data.favorites.HttpSavedPlaceRepository
import com.flowyride.data.geocoding.photon.PhotonConfig
import com.flowyride.data.geocoding.photon.PhotonGeocoder
import com.flowyride.data.routing.flowyride.FlowyRideServerEngine
import com.flowyride.data.routing.flowyride.FlowyRideServerLoopGenerator
import com.flowyride.data.speedcameras.datagouv.DataGouvConfig
import com.flowyride.data.speedcameras.datagouv.DataGouvSpeedCameraRepository
import com.flowyride.data.speedcameras.flowyride.FallbackSpeedCameraRepository
import com.flowyride.data.speedcameras.flowyride.FlowyRideServerSpeedCameraRepository
import com.flowyride.feature.account.AdminViewModel
import com.flowyride.feature.account.AuthViewModel
import com.flowyride.feature.account.ChangePasswordViewModel
import com.flowyride.feature.account.SessionViewModel
import com.flowyride.feature.settings.SettingsViewModel
import com.flowyride.feature.navigation.NavigationViewModel
import com.flowyride.feature.planner.PlannerViewModel
import io.ktor.client.HttpClient
import org.koin.core.module.Module
import org.koin.core.module.dsl.factoryOf
import org.koin.core.module.dsl.viewModel
import org.koin.core.module.dsl.viewModelOf
import org.koin.core.qualifier.named
import org.koin.dsl.bind
import org.koin.dsl.module
import kotlin.time.Clock

/** Top-level properties initialise in order: declared before the modules that use it. */
private val ServerClient = named("flowyRideServer")

private val networkModule = module {
    single { NetworkConfig(userAgent = "FlowyRide/0.1 (+https://github.com/cedricnanteau)") }
    // Third-party APIs (Photon, data.gouv): never carries the session.
    single { createHttpClient(get()) }
    single { ServerSession(platformTokenStore()) }
    // FlowyRide server only: carries the session token.
    single(ServerClient) { get<HttpClient>().withServerSession(get()) }
}

private val dataModule = module {
    single { PhotonGeocoder(get(), PhotonConfig()) } bind Geocoder::class
    single { FlowyRideServerEngine(get(ServerClient), serverUrl()) }
    single<LoopGenerator> { FlowyRideServerLoopGenerator(get(ServerClient), serverUrl()) }
    single<AccountRepository> { HttpAccountRepository(get(ServerClient), serverUrl(), get()) }
    single<AdminRepository> { HttpAdminRepository(get(ServerClient), serverUrl()) }
    single<FavoriteRepository> { HttpFavoriteRepository(get(ServerClient), serverUrl()) }
    single<SavedPlaceRepository> { HttpSavedPlaceRepository(get(ServerClient), serverUrl()) }
    single { SavedPlaces(get()) }
    // Display order in the engine picker (hidden while there is a single engine).
    single { RouteEngineRegistry(listOf(get<FlowyRideServerEngine>())) }
    // The server's list has the light vehicles' limits; data.gouv stands in if it cannot answer.
    single<SpeedCameraRepository> {
        FallbackSpeedCameraRepository(
            primary = FlowyRideServerSpeedCameraRepository(get(ServerClient), serverUrl()),
            fallback = DataGouvSpeedCameraRepository(get(), DataGouvConfig()),
        )
    }
    single<NavigationDevice> { platformNavigationDevice() }
    single<NavigationPreferences> { NavigationSettings() }
}

private val domainModule = module {
    single { GpxWriter() }
    factoryOf(::SearchPlacesUseCase)
    factory { ComputeRouteUseCase(get(), get()) }
    factoryOf(::GenerateLoopUseCase)
    factoryOf(::BuildGpxUseCase)
    factoryOf(::ReverseGeocodeUseCase)
    factoryOf(::GetSpeedCamerasUseCase)
    factory { ReadGpxUseCase() }
    single { ActiveNavigation() }
    // Files handed over by the system ("Ouvrir avec"), waiting for the planner.
    single { OpenedFiles() }
}

private val featureModule = module {
    // Explicit: viewModelOf would try to resolve the optional seed source from Koin.
    viewModel {
        PlannerViewModel(
            get(), get(), get(), get(), get(), get(), get(), get(), get(), get(), get(), get(),
            canNavigate = get<NavigationDevice>().supportsNavigation,
            location = get<NavigationDevice>().roughLocation,
            hasLocationPermission = { get<NavigationDevice>().hasLocationPermission },
            guidance = get<FlowyRideServerEngine>(),
        )
    }
    viewModel {
        NavigationViewModel(
            activeNavigation = get(),
            device = get(),
            engine = get<FlowyRideServerEngine>(),
            guidance = get<FlowyRideServerEngine>(),
            settings = get(),
            now = { Clock.System.now().toEpochMilliseconds() },
        )
    }
    viewModelOf(::SessionViewModel)
    viewModelOf(::AuthViewModel)
    viewModelOf(::AdminViewModel)
    viewModelOf(::ChangePasswordViewModel)
    viewModelOf(::SettingsViewModel)
}

val appModules: List<Module> = listOf(networkModule, dataModule, domainModule, featureModule)

/**
 * The web app always calls its own origin: its session cookie is same-site only.
 * Native apps use the build setting.
 */
private fun serverUrl(): String = platformDefaultServerUrl() ?: AppConfig.SERVER_URL
